package com.dotdrives.repository

import com.dotdrives.client.AuthClient
import com.dotdrives.model.AuthProvider
import com.dotdrives.model.AuthSessionSnapshot
import com.dotdrives.model.SessionInfo
import com.dotdrives.model.SignUpOutcome
import kotlinx.coroutines.flow.Flow
import kotlin.time.Clock

interface RecoveryGrantStore {
    fun expiresAt(userId: String): Long?

    fun save(userId: String, expiresAt: Long)

    fun clear(userId: String)
}

object NoOpRecoveryGrantStore : RecoveryGrantStore {
    override fun expiresAt(userId: String): Long? = null

    override fun save(userId: String, expiresAt: Long) = Unit

    override fun clear(userId: String) = Unit
}

internal class RecoveryGrantCoordinator(
    private val store: RecoveryGrantStore,
    private val ttlMillis: Long,
    private val nowMillis: () -> Long,
) {
    fun resolve(snapshot: AuthSessionSnapshot): AuthSessionSnapshot {
        val session = snapshot.session ?: return snapshot
        val now = nowMillis()
        if (snapshot.source == com.dotdrives.model.AuthSessionSource.Recovery) {
            store.save(session.userId, now + ttlMillis)
            return snapshot
        }
        val expiresAt = store.expiresAt(session.userId) ?: return snapshot
        if (expiresAt <= now) {
            store.clear(session.userId)
            return snapshot
        }
        return snapshot.copy(source = com.dotdrives.model.AuthSessionSource.Recovery)
    }
}

interface AuthRepository {
    val session: Flow<SessionInfo?>

    suspend fun signIn(email: String, password: String): Result<SessionInfo>

    suspend fun signUp(email: String, password: String, redirectUrl: String? = null): Result<SignUpOutcome>

    suspend fun signInWithProvider(provider: AuthProvider, redirectUrl: String): Result<Unit>

    suspend fun sendMagicLink(email: String, redirectUrl: String): Result<Unit>

    suspend fun sendPasswordReset(email: String, redirectUrl: String): Result<Unit>

    suspend fun verifyRecoveryToken(tokenHash: String): Result<SessionInfo>

    suspend fun updatePassword(newPassword: String): Result<Unit>

    suspend fun signOut(): Result<Unit>

    suspend fun awaitInitialSession(): AuthSessionSnapshot

    fun grantRecovery(session: SessionInfo): AuthSessionSnapshot

    fun clearRecoveryGrant()

    fun hasValidRecoveryGrant(userId: String): Boolean
}

class SupabaseAuthRepository(
    private val client: AuthClient = AuthClient(),
    private val recoveryGrantStore: RecoveryGrantStore = NoOpRecoveryGrantStore,
    private val recoveryGrantTtlMillis: Long = 10 * 60_000L,
    private val nowMillis: () -> Long = {
        Clock.System.now().toEpochMilliseconds()
    },
) : AuthRepository {
    override val session: Flow<SessionInfo?> = client.session
    private val recoveryGrants = RecoveryGrantCoordinator(
        store = recoveryGrantStore,
        ttlMillis = recoveryGrantTtlMillis,
        nowMillis = nowMillis,
    )

    override suspend fun signIn(email: String, password: String): Result<SessionInfo> =
        runCatching { client.signIn(email, password) }

    override suspend fun signUp(email: String, password: String, redirectUrl: String?): Result<SignUpOutcome> =
        runCatching { client.signUp(email, password, redirectUrl) }

    override suspend fun signInWithProvider(
        provider: AuthProvider,
        redirectUrl: String,
    ): Result<Unit> = runCatching { client.signInWithProvider(provider, redirectUrl) }

    override suspend fun sendMagicLink(email: String, redirectUrl: String): Result<Unit> =
        runCatching { client.sendMagicLink(email, redirectUrl) }

    override suspend fun sendPasswordReset(email: String, redirectUrl: String): Result<Unit> =
        runCatching { client.sendPasswordReset(email, redirectUrl) }

    override suspend fun verifyRecoveryToken(tokenHash: String): Result<SessionInfo> =
        runCatching { client.verifyRecoveryToken(tokenHash) }
            .onSuccess { session ->
                recoveryGrantStore.save(
                    session.userId,
                    nowMillis() + recoveryGrantTtlMillis,
                )
            }

    override suspend fun updatePassword(newPassword: String): Result<Unit> =
        runCatching { client.updatePassword(newPassword) }
            .onSuccess { clearRecoveryGrant() }

    override suspend fun signOut(): Result<Unit> {
        clearRecoveryGrant()
        return runCatching { client.signOut() }
    }

    override suspend fun awaitInitialSession(): AuthSessionSnapshot {
        return recoveryGrants.resolve(client.awaitInitialSession())
    }

    override fun grantRecovery(session: SessionInfo): AuthSessionSnapshot {
        recoveryGrantStore.save(
            session.userId,
            nowMillis() + recoveryGrantTtlMillis,
        )
        return AuthSessionSnapshot(session, com.dotdrives.model.AuthSessionSource.Recovery)
    }

    override fun clearRecoveryGrant() {
        client.currentSession()?.userId?.let(recoveryGrantStore::clear)
    }

    override fun hasValidRecoveryGrant(userId: String): Boolean {
        val expiresAt = recoveryGrantStore.expiresAt(userId) ?: return false
        if (expiresAt <= nowMillis()) {
            recoveryGrantStore.clear(userId)
            return false
        }
        return true
    }
}
